// Regras puras da tela de webhooks: catálogo de eventos, estado do endpoint, estado da
// entrega e a previsão da próxima tentativa. Sem React, para o teste rodar sem provider
// nenhum (`spec/17` §11.1).

import { errorMessageOf, isApiError } from '../../api/errors.ts';
import type { Delivery, DeliveryStatus, WebhookEndpoint } from '../../api/queries/webhooks.ts';
import { formatDate, formatShortDate, formatTime } from '../../lib/dates.ts';

/**
 * Vocabulário de tom independente de `kit` (banido nas telas Zenith): os
 * mesmos quatro valores de `TimelineTone` (`components/app/timeline.tsx`), então
 * `deliveryTimeline`/`attemptTimeline` passam o tom direto, sem tradução. Para `Badge`
 * (`components/ui/badge.tsx`), `STATUS_BADGE_VARIANT` faz a ponte.
 */
export type StatusTone = 'ok' | 'bad' | 'wait' | 'neutral';

export const STATUS_BADGE_VARIANT: Record<
  StatusTone,
  'success' | 'destructive' | 'warning' | 'secondary'
> = {
  ok: 'success',
  bad: 'destructive',
  wait: 'warning',
  neutral: 'secondary',
};

/**
 * `PUBLIC_EVENT_TYPES` de `packages/events/src/catalog.ts`; copiado porque o front não
 * importa pacote de servidor (mesma razão de `WITHDRAWAL_TYPES` em `app/live-events.ts`).
 * `ping` não é assinável (`spec/07` §4.5) e evento interno nunca vira webhook.
 */
export const PUBLIC_EVENT_TYPES = [
  'transaction.paid',
  'transaction.expired',
  'transaction.failed',
  'transaction.refunded',
  'transaction.chargeback',
  'transaction.reserve_released',
  'withdrawal.awaiting',
  'withdrawal.approved',
  'withdrawal.rejected',
  'withdrawal.processing',
  'withdrawal.successful',
  'withdrawal.failed',
  'withdrawal.cancelled',
  'seller.kyc_approved',
  'seller.kyc_rejected',
  'seller.payout_locked',
  'seller.payout_unlocked',
] as const;

export type PublicEventType = (typeof PUBLIC_EVENT_TYPES)[number];

/** `MAX_ACTIVE_ENDPOINTS_PER_MODE` do service (`spec/07` §4.5). Hardcode: `apps/web` não
 * importa de `apps/api`. O `409 invalid_state` continua sendo a rede de segurança. */
export const MAX_ENDPOINTS = 5;

/** Curinga aceito pela API em `events[]`. */
export const ALL_EVENTS = '*';

export interface EndpointState {
  tone: StatusTone;
  label: string;
  hint: string;
}

/**
 * Três estados, desde API-25: ativo, desabilitado automático e desabilitado (manual).
 * Quem distingue é `disabled_reason`, `auto` é a desativação de `spec/07` §4.3 (50
 * entregas mortas sem sucesso em 7 dias), `manual` é o `PATCH` do próprio seller.
 * `null` com `enabled = false` é linha antiga, anterior à `0020`: cai no texto neutro.
 */
export function endpointState(endpoint: WebhookEndpoint): EndpointState {
  if (endpoint.enabled) {
    return {
      tone: 'ok',
      label: 'ativo',
      hint: 'Recebe as entregas dos eventos assinados.',
    };
  }
  if (endpoint.disabled_reason === 'auto') {
    return {
      tone: 'bad',
      label: 'desabilitado automático',
      hint: '50 entregas dead consecutivas sem sucesso em 7 dias. Corrija o endpoint e reative manualmente.',
    };
  }
  return {
    tone: 'neutral',
    label: 'desabilitado',
    hint: 'Não recebe entrega nenhuma até ser reativado.',
  };
}

export function isAutoDisabled(endpoint: WebhookEndpoint): boolean {
  return !endpoint.enabled && endpoint.disabled_reason === 'auto';
}

/** Copy do croqui para o `Notice` do topo, no plural certo. */
export function autoDisabledTitle(count: number): string {
  return count === 1
    ? '1 endpoint desabilitado automaticamente.'
    : `${count} endpoints desabilitados automaticamente.`;
}

/**
 * A pílula do CROQUI: caixa baixa e "tentando" no `failed` (ainda há retry automático
 * pela frente, a 6ª falha vira `dead` no servidor, então não existe `failed` com
 * `attempt` esgotado).
 *
 */
const CROQUI_LABELS: Record<DeliveryStatus, { tone: StatusTone; label: string }> = {
  success: { tone: 'ok', label: 'entregue' },
  failed: { tone: 'wait', label: 'tentando' },
  dead: { tone: 'bad', label: 'morta' },
};

export function deliveryStatusPill(status: string): { tone: StatusTone; label: string } {
  return status === 'success' || status === 'failed' || status === 'dead'
    ? CROQUI_LABELS[status]
    : { tone: 'neutral', label: status };
}

/** Os três `status` de `WebhookDelivery` (`spec/07` §4.3), para o schema da query string. */
export const DELIVERY_STATUSES = ['success', 'failed', 'dead'] as const;

/** Abas do croqui em Entregas, mapeadas 1:1 no `status` do servidor (`?status=`). */
export const DELIVERY_TABS = [
  { id: 'all', label: 'Todas' },
  { id: 'success', label: 'Entregues' },
  { id: 'failed', label: 'Tentando' },
  { id: 'dead', label: 'Mortas' },
] as const;

export type DeliveryTab = (typeof DELIVERY_TABS)[number]['id'];

export function tabOf(status: DeliveryStatus | undefined): DeliveryTab {
  return status ?? 'all';
}

export function statusOfTab(tab: string): DeliveryStatus | undefined {
  return tab === 'success' || tab === 'failed' || tab === 'dead' ? tab : undefined;
}

/**
 * Busca da barra do croqui ("Buscar por evento ou id da entrega"). É CLIENTE, sobre o que
 * já foi carregado: `GET /v1/me/webhook-deliveries` não tem busca textual (só `event_type`
 * exato). O vazio da tabela diz isso em vez de deixar o usuário achar que varreu tudo.
 */
export function matchesSearch(delivery: Delivery, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return (
    delivery.event_type.toLowerCase().includes(needle) ||
    delivery.id.toLowerCase().includes(needle) ||
    delivery.target_host.toLowerCase().includes(needle)
  );
}

/** `SCHEDULE` de `spec/07` §4.3, em segundos, indexado por `attempt - 1`. */
export const RETRY_SCHEDULE_S = [60, 300, 1800, 7200, 43200, 86400] as const;

export const MAX_ATTEMPTS = RETRY_SCHEDULE_S.length;

/**
 * A lista de entregas não traz `next_attempt_at` (a coluna vive no job do pg-boss, não na
 * linha da entrega, ver "Questões abertas"). A previsão é derivada de `created_at` + a
 * `SCHEDULE` da tentativa, e a tela rotula como previsão, não como fato.
 */
export function nextAttemptAt(delivery: Delivery): Date | undefined {
  if (delivery.status !== 'failed' || delivery.attempt >= MAX_ATTEMPTS) {
    return undefined;
  }
  const delay = RETRY_SCHEDULE_S[delivery.attempt - 1];
  if (delay === undefined) {
    return undefined;
  }
  return new Date(new Date(delivery.created_at).getTime() + delay * 1000);
}

/**
 * Croqui: "às 15:10" na coluna "Próximo retry" (110px). Só a hora quando a tentativa cai
 * no mesmo dia; nos degraus longos do backoff (12 h, 24 h) a data volta, senão "às 03:20"
 * mentiria dizendo "hoje".
 */
export function nextAttemptLabel(delivery: Delivery, now: Date = new Date()): string {
  const at = nextAttemptAt(delivery);
  if (at === undefined) {
    return '-';
  }
  return formatDate(at) === formatDate(now)
    ? `às ${formatTime(at)}`
    : `${formatShortDate(at)} ${formatTime(at)}`;
}

export function attemptLabel(delivery: Delivery): string {
  return `${delivery.attempt}/${MAX_ATTEMPTS}`;
}

/** Os três alvos de entrega: endpoint do seller, postback da cobrança (`spec/07` §4.2) e
 * a UTMify (`spec/18` §5). */
export const DELIVERY_TARGETS = ['endpoint', 'postback', 'utmify'] as const;

export type DeliveryTarget = (typeof DELIVERY_TARGETS)[number];

export const TARGET_FILTER_LABEL: Record<DeliveryTarget, string> = {
  endpoint: 'Endpoints',
  postback: 'Postback',
  utmify: 'UTMify',
};

export function targetLabel(delivery: Pick<Delivery, 'target' | 'target_host'>): string {
  if (delivery.target === 'postback') {
    return `postback · ${delivery.target_host}`;
  }
  if (delivery.target === 'utmify') {
    return `UTMify · ${delivery.target_host}`;
  }
  return delivery.target_host;
}

/**
 * O step-up cancelado (o usuário fechou o `StepUpDialog`) volta como o `mfa_required`
 * original de `api/client.ts`. Não é erro de tela: some sem mensagem vermelha.
 */
export function isStepUpCancelled(error: unknown): boolean {
  return isApiError(error) && error.code === 'mfa_required';
}

/** `400 invalid_url` pertence ao campo da URL, não ao rodapé do dialog (item 2 da task). */
export function isInvalidUrlError(error: unknown): boolean {
  return isApiError(error) && error.code === 'invalid_url';
}

/**
 * Mensagem do formulário. `invalid_url` ganha o motivo do servidor mais a regra em
 * pt-BR (a razão vem em inglês curto e não é contrato de UI, `spec/17` §4.3 item 1 -
 * então a decisão é pelo `code` e o texto do servidor entra como detalhe).
 */
export function endpointErrorMessage(error: unknown): string {
  if (!isApiError(error)) {
    return errorMessageOf(error);
  }
  switch (error.code) {
    case 'invalid_url':
      return `URL recusada: precisa ser https, na porta padrão, sem usuário e senha, e apontar para um endereço público. ${errorMessageOf(error)}`;
    case 'invalid_event_type':
      return `Tipo de evento fora do catálogo. ${errorMessageOf(error)}`;
    case 'invalid_state':
      return `Limite de ${MAX_ENDPOINTS} endpoints ativos neste modo. Remova um antes de criar outro. ${errorMessageOf(error)}`;
    default:
      return errorMessageOf(error);
  }
}

/** `409 invalid_state` no retry: já entregue, ou já tem tentativa agendada. */
export function retryErrorMessage(error: unknown): string {
  if (isApiError(error) && error.code === 'invalid_state') {
    return `Não dá para reenviar esta entrega. ${errorMessageOf(error)}`;
  }
  return errorMessageOf(error);
}

export interface PingResult {
  endpointId: string;
  ok: boolean;
  message: string;
}

/**
 * O `ping` é assíncrono: a API responde `202` com o id da entrega e quem entrega é o
 * worker (`spec/07` §4.5). A tela diz isso em vez de prometer um resultado que ela
 * ainda não tem.
 */
export function pingSentMessage(deliveryId: string): string {
  return `Entrega de teste enfileirada (${deliveryId}). O resultado aparece em Entregas em alguns segundos.`;
}
